import { useState } from 'react';
import { AppBar, Toolbar, Box, IconButton, Avatar, Typography, Menu, MenuItem, Divider, ListItemIcon, ListItemText, Drawer, Stack, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Bell, HelpCircle, User, Settings as SettingsIcon, LogOut, X, CheckCheck, MessageSquare, Gift, AlertCircle, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../services/authService';

const StyledAppBar = styled(AppBar)(() => ({
  backgroundColor: 'white',
  color: '#111827',
  boxShadow: 'none',
  borderBottom: '1px solid #EAECEF',
  position: 'sticky',
  top: 0,
  zIndex: 1100
}));

interface DashboardHeaderProps {
  businessName?: string; // Keep for backwards compatibility but not used in display
  userAvatar: string;
  businessLogo?: string | null; // Keep for backwards compatibility but not used in display
}

const DashboardHeader = ({ userAvatar }: DashboardHeaderProps) => {
  const navigate = useNavigate();
  const [profileAnchorEl, setProfileAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationDrawerOpen, setNotificationDrawerOpen] = useState(false);

  // Mock notification data
  const notifications = [
    {
      id: 1,
      type: 'survey',
      title: 'Survey Completed',
      message: 'John Doe completed the product feedback survey',
      time: '5 minutes ago',
      read: false,
      icon: MessageSquare,
      color: '#4F46E5'
    },
    {
      id: 2,
      type: 'reward',
      title: 'Reward Claimed',
      message: 'Sarah Smith claimed $10 reward for their review',
      time: '1 hour ago',
      read: false,
      icon: Gift,
      color: '#10B981'
    },
    {
      id: 3,
      type: 'review',
      title: 'New Review Posted',
      message: 'A new 5-star review was posted on Google',
      time: '2 hours ago',
      read: true,
      icon: Star,
      color: '#F59E0B'
    },
    {
      id: 4,
      type: 'alert',
      title: 'Negative Review Alert',
      message: 'A 2-star review needs your attention',
      time: '3 hours ago',
      read: true,
      icon: AlertCircle,
      color: '#EF4444'
    },
    {
      id: 5,
      type: 'survey',
      title: 'Survey Created',
      message: 'New product survey has been created and sent',
      time: '1 day ago',
      read: true,
      icon: CheckCheck,
      color: '#6366F1'
    }
  ];

  const unreadCount = notifications.filter(n => !n.read).length;

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileClose = () => {
    setProfileAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      handleProfileClose();
      navigate('/signin');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleProfile = () => {
    handleProfileClose();
    navigate('/settings', { state: { tab: 0 } }); // Navigate to Settings with Profile tab (index 0)
  };

  const handleSettings = () => {
    handleProfileClose();
    navigate('/settings'); // Navigate to Settings page
  };

  return (
    <StyledAppBar>
      <Toolbar sx={{ px: 4, minHeight: 64 }}>
        {/* Empty space on the left - logo and name are now in sidebar */}
        <Box sx={{ flex: 1 }} />

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Notification bell hidden for now */}
          <IconButton
            onClick={() => navigate('/help')}
            sx={{
              width: 40,
              height: 40,
              bgcolor: 'white',
              borderRadius: '50%',
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: '#F3F4F6'
              }
            }}
          >
            <HelpCircle size={20} color="#374151" />
          </IconButton>
          <IconButton
            onClick={handleProfileClick}
            sx={{
              p: 0,
              ml: 1,
              '&:hover': {
                backgroundColor: 'transparent'
              }
            }}
          >
            <Avatar
              src={userAvatar}
              sx={{
                width: 40,
                height: 40,
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: '0 0 0 2px #4F46E5'
                }
              }}
            />
          </IconButton>
          
          {/* Profile Menu */}
          <Menu
            anchorEl={profileAnchorEl}
            open={Boolean(profileAnchorEl)}
            onClose={handleProfileClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 200
              }
            }}
          >
            <MenuItem onClick={handleProfile}>
              <ListItemIcon>
                <User size={18} />
              </ListItemIcon>
              <ListItemText>Profile</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleSettings}>
              <ListItemIcon>
                <SettingsIcon size={18} />
              </ListItemIcon>
              <ListItemText>Settings</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem 
              onClick={handleLogout}
              sx={{
                color: '#EF4444',
                '&:hover': {
                  backgroundColor: '#FEE2E2'
                }
              }}
            >
              <ListItemIcon>
                <LogOut size={18} color="#EF4444" />
              </ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>

          {/* Notification Drawer */}
          <Drawer
            anchor="right"
            open={notificationDrawerOpen}
            onClose={() => setNotificationDrawerOpen(false)}
            PaperProps={{
              sx: {
                width: { xs: '100%', sm: 420 },
                bgcolor: '#F9FAFB'
              }
            }}
          >
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {/* Header */}
              <Box
                sx={{
                  p: 3,
                  bgcolor: 'white',
                  borderBottom: '1px solid #E5E7EB',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: '1.25rem',
                      fontWeight: 700,
                      color: '#111827'
                    }}
                  >
                    Notifications
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      color: '#6B7280',
                      mt: 0.5
                    }}
                  >
                    You have {unreadCount} unread {unreadCount === 1 ? 'notification' : 'notifications'}
                  </Typography>
                </Box>
                <IconButton
                  onClick={() => setNotificationDrawerOpen(false)}
                  sx={{
                    color: '#6B7280',
                    '&:hover': {
                      bgcolor: '#F3F4F6'
                    }
                  }}
                >
                  <X size={20} />
                </IconButton>
              </Box>

              {/* Notifications List */}
              <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                <Stack spacing={1.5}>
                  {notifications.map((notification) => {
                    const IconComponent = notification.icon;
                    return (
                      <Box
                        key={notification.id}
                        sx={{
                          bgcolor: 'white',
                          borderRadius: '12px',
                          p: 2.5,
                          border: '1px solid #E5E7EB',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          position: 'relative',
                          '&:hover': {
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                            borderColor: '#D1D5DB'
                          }
                        }}
                      >
                        {!notification.read && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 12,
                              right: 12,
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: '#4F46E5'
                            }}
                          />
                        )}
                        <Stack direction="row" spacing={2}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '8px',
                              bgcolor: `${notification.color}15`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}
                          >
                            <IconComponent size={20} color={notification.color} />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                color: '#111827',
                                mb: 0.5
                              }}
                            >
                              {notification.title}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '0.875rem',
                                color: '#6B7280',
                                lineHeight: 1.5,
                                mb: 1
                              }}
                            >
                              {notification.message}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '0.75rem',
                                color: '#9CA3AF'
                              }}
                            >
                              {notification.time}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>

              {/* Footer */}
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'white',
                  borderTop: '1px solid #E5E7EB'
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: '#4F46E5',
                    textAlign: 'center',
                    cursor: 'pointer',
                    py: 1,
                    '&:hover': {
                      color: '#4338CA'
                    }
                  }}
                  onClick={() => {
                    setNotificationDrawerOpen(false);
                    navigate('/notifications');
                  }}
                >
                  View All Notifications
                </Typography>
              </Box>
            </Box>
          </Drawer>
        </Box>
      </Toolbar>
    </StyledAppBar>
  );
};

export default DashboardHeader;